import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Youtube, Play, Radio } from "lucide-react";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { NotifyCTA } from "@/components/NotifyCTA";

const CATS = [
  { id: "all", label: "All" },
  { id: "league_match", label: "League Matches" },
  { id: "braggers", label: "Bragger's Cup" },
  { id: "tournament", label: "Tournaments" },
  { id: "challenge", label: "Challenge Matches" },
  { id: "special", label: "Special Events" },
];

export default function Videos() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/videos/library").then((r) => setData(r.data)).catch(() => setData({ videos: [] }));
  }, []);

  const all = data?.videos || [];
  const channelUrl = data?.channel_url || "https://www.youtube.com/@QuinteBCA";
  const videos = filter === "all" ? all : all.filter((v) => v.category === filter);
  const featured = all[0];
  const firstLine = (d) => (d || "").split("\n").map((s) => s.trim()).filter(Boolean)[0] || "";
  const catsPresent = CATS.filter((c) => c.id === "all" || all.some((v) => v.category === c.id));

  return (
    <Layout>
      <Seo title="Pool Matches, Live Streams & Tournament Replays | Quinte BCA" description="Watch Quinte BCA league matches, pool tournaments, challenge matches, live streams and billiards replays from Eastern Ontario." path="/videos" />
      <PageHeader
        eyebrow="Watch & re-watch"
        title="Videos"
        subtitle={data?.intro || "We live stream our league nights and tournaments to YouTube. Catch the latest streams and replays below."}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="flex flex-wrap items-center gap-4 mb-10">
          <a href={channelUrl} target="_blank" rel="noopener noreferrer" data-testid="yt-channel-link"
            className="inline-flex items-center gap-2 bg-[#E11D2A] text-white rounded-md px-5 py-3 font-semibold hover:bg-[#c41320] transition-colors">
            <Youtube className="h-5 w-5" /> Visit our YouTube channel
          </a>
          <a href={`${channelUrl}/streams`} target="_blank" rel="noopener noreferrer" data-testid="yt-live-link"
            className="inline-flex items-center gap-2 border border-[#E11D2A] text-[#E11D2A] rounded-md px-5 py-3 font-semibold hover:bg-red-50 transition-colors">
            <Radio className="h-5 w-5" /> Watch live streams
          </a>
        </div>

        {!data ? (
          <p className="text-[#555555]">Loading videos…</p>
        ) : all.length === 0 ? (
          <p className="text-[#555555]">Videos will appear here soon.</p>
        ) : (
          <>
            {featured && (
              <div className="mb-14">
                <h2 className="font-heading text-2xl font-bold mb-4">Latest</h2>
                <Link to={`/videos/${featured.slug}`} data-testid="video-featured" className="group block max-w-4xl">
                  <div className="relative aspect-video w-full rounded-xl overflow-hidden shadow-lg border border-[#E5E7EB] bg-black">
                    <img src={featured.thumbnail} alt={featured.title} className="w-full h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/45 transition-colors">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#E11D2A] text-white"><Play className="h-7 w-7 ml-1" /></span>
                    </span>
                    {featured.is_live && <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#E11D2A] text-white rounded-full px-2.5 py-1 text-xs font-bold uppercase"><Radio className="h-3 w-3" /> Live</span>}
                  </div>
                  <p className="mt-3 font-semibold text-[#111111] group-hover:text-[#1E63C4] transition-colors">{featured.title}</p>
                  {firstLine(featured.description) && <p className="text-sm text-[#555555] mt-1 max-w-3xl line-clamp-2">{firstLine(featured.description)}</p>}
                </Link>
              </div>
            )}

            {catsPresent.length > 2 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="video-filters">
                {catsPresent.map((c) => (
                  <button key={c.id} onClick={() => setFilter(c.id)} data-testid={`video-filter-${c.id}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${filter === c.id ? "bg-[#111111] text-white border-[#111111]" : "border-[#E5E7EB] text-[#555] hover:bg-[#F9FAF8]"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="videos-grid">
              {videos.map((v) => (
                <Link key={v.slug} to={`/videos/${v.slug}`} data-testid={`video-card-${v.slug}`}
                  className="group text-left bg-white rounded-lg border border-[#E5E7EB] shadow-sm overflow-hidden hover:shadow-lg transition-all">
                  <div className="relative aspect-video bg-black">
                    <img src={v.thumbnail} alt={v.title} loading="lazy" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E11D2A] text-white"><Play className="h-6 w-6 ml-0.5" /></span>
                    </span>
                    {v.is_live && <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#E11D2A] text-white rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"><Radio className="h-2.5 w-2.5" /> Live</span>}
                  </div>
                  <div className="p-4">
                    <p className="font-semibold text-[#111111] line-clamp-2 group-hover:text-[#1E63C4] transition-colors">{v.title}</p>
                    {firstLine(v.description) && <p className="text-xs text-[#555555] mt-1 line-clamp-2">{firstLine(v.description)}</p>}
                    {v.date && <p className="text-xs text-[#999999] mt-1.5">{new Date(v.date).toLocaleDateString()}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
      <NotifyCTA title="Get notified when we go live" text="Subscribe to Live stream alerts and we'll email you the moment Quinte BCA goes live on YouTube — so you never miss the action." />
    </Layout>
  );
}

